import { useState, useEffect } from 'react';
import { Save, Building, Phone, FileText, ShoppingBag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ImageUpload from '@/components/ImageUpload';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ContentField({ label, fieldKey, content, setContent, type = 'text', rows = 2 }) {
  const val = content[fieldKey] || '';
  const update = (v) => setContent(prev => ({ ...prev, [fieldKey]: v }));
  return (
    <div>
      <Label>{label}</Label>
      {type === 'textarea' ? (
        <Textarea value={val} onChange={e => update(e.target.value)} rows={rows} className="bg-white" />
      ) : type === 'image' ? (
        <ImageUpload value={val} onChange={update} label="" />
      ) : (
        <Input value={val} onChange={e => update(e.target.value)} className="bg-white" />
      )}
    </div>
  );
}

export default function AdminContent() {
  const [content, setContent] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/site-content`).then(r => {
      setContent(r.data);
    }).catch(() => {});
  }, []);

  const saveAll = async () => {
    setSaving(true);
    try {
      const entries = Object.entries(content);
      for (const [key, value] of entries) {
        const contentType = key.includes('image') ? 'image' : 'text';
        await axios.put(`${API}/admin/site-content`, { key, value, content_type: contentType });
      }
      toast.success('Content saved successfully');
    } catch {
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-content">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold" style={{ fontFamily: "'Poppins', sans-serif" }}>Site Content</h1>
        <Button onClick={saveAll} disabled={saving} className="btn-gold text-sm" data-testid="save-content-btn">
          <Save size={16} className="mr-2" />{saving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </div>

      <Tabs defaultValue="about" className="space-y-4">
        <TabsList>
          <TabsTrigger value="about">About Us</TabsTrigger>
          <TabsTrigger value="contact">Contact Info</TabsTrigger>
          <TabsTrigger value="sell">Sell Page</TabsTrigger>
          <TabsTrigger value="education">Education</TabsTrigger>
        </TabsList>

        <TabsContent value="about">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><Building size={18} /> About Us Page</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <ContentField label="Company Name" fieldKey="about_company_name" content={content} setContent={setContent} />
              <ContentField label="Tagline / Subtitle" fieldKey="about_tagline" content={content} setContent={setContent} />
              <ContentField label="Main Description" fieldKey="about_description" content={content} setContent={setContent} type="textarea" rows={5} />
              <ContentField label="Mission Statement" fieldKey="about_mission" content={content} setContent={setContent} type="textarea" rows={3} />
              <ContentField label="Year Established" fieldKey="about_year_established" content={content} setContent={setContent} />
              <ContentField label="About Page Image" fieldKey="about_image" content={content} setContent={setContent} type="image" />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contact">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><Phone size={18} /> Contact Information</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ContentField label="Phone Number" fieldKey="contact_phone" content={content} setContent={setContent} />
                <ContentField label="Email Address" fieldKey="contact_email" content={content} setContent={setContent} />
                <ContentField label="Street Address" fieldKey="contact_address" content={content} setContent={setContent} />
                <ContentField label="City / Region" fieldKey="contact_city" content={content} setContent={setContent} />
              </div>
              <ContentField label="Store Hours" fieldKey="contact_hours" content={content} setContent={setContent} type="textarea" rows={3} />
              <ContentField label="Google Maps Embed URL" fieldKey="contact_map_url" content={content} setContent={setContent} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sell">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><ShoppingBag size={18} /> Sell Page</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-gray-400 border-b pb-2 mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>How It Works — 4 Steps</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ContentField label="Step 1 Title" fieldKey="sell_step1_title" content={content} setContent={setContent} />
                <ContentField label="Step 1 Description" fieldKey="sell_step1_desc" content={content} setContent={setContent} />
                <ContentField label="Step 2 Title" fieldKey="sell_step2_title" content={content} setContent={setContent} />
                <ContentField label="Step 2 Description" fieldKey="sell_step2_desc" content={content} setContent={setContent} />
                <ContentField label="Step 3 Title" fieldKey="sell_step3_title" content={content} setContent={setContent} />
                <ContentField label="Step 3 Description" fieldKey="sell_step3_desc" content={content} setContent={setContent} />
                <ContentField label="Step 4 Title" fieldKey="sell_step4_title" content={content} setContent={setContent} />
                <ContentField label="Step 4 Description" fieldKey="sell_step4_desc" content={content} setContent={setContent} />
              </div>
              <p className="text-xs text-gray-400 border-b pb-2 mb-2 mt-6" style={{ fontFamily: "'Poppins', sans-serif" }}>What We Buy List (one item per line)</p>
              <ContentField label="What We Buy" fieldKey="sell_what_we_buy" content={content} setContent={setContent} type="textarea" rows={5} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="education">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><FileText size={18} /> Education Hub Settings</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <ContentField label="Section Title" fieldKey="education_title" content={content} setContent={setContent} />
              <ContentField label="Section Subtitle" fieldKey="education_subtitle" content={content} setContent={setContent} type="textarea" rows={2} />
              <ContentField label="Featured Video URL" fieldKey="education_featured_video" content={content} setContent={setContent} />
              <ContentField label="Hero Image" fieldKey="education_hero_image" content={content} setContent={setContent} type="image" />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
